import React, { Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Sparkles, Wrench, Users } from "lucide-react";
import Car3D from "../components/site/Car3D";
import VehicleCard from "../components/site/VehicleCard";
import { listVehicles } from "../lib/api";
import { useSettings } from "../lib/settings";

const stats = (s) => [
  { k: s.rating || "4.6★", v: "Google rating" },
  { k: "Madison", v: "TN — Metro Nashville" },
  { k: "Used", v: "Cars · trucks · SUVs" },
  { k: "9-6", v: "Open Mon – Sat" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { settings } = useSettings();
  useEffect(() => {
    listVehicles({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden">
        <div className="hero-flare" style={{ top: "-200px", left: "-200px" }} />
        <div className="hero-flare" style={{ bottom: "-300px", right: "-200px", background: "radial-gradient(circle, rgba(80,80,120,0.25) 0%, rgba(0,0,0,0) 60%)" }} />

        {/* Massive typography behind 3D */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 px-4">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="hero-title"
            className="font-display text-[clamp(80px,20vw,320px)] leading-none tracking-tighter text-white/[0.07] select-none"
          >
            PICK AUTO
          </motion.h1>
        </div>

        {/* 3D Canvas */}
        <div className="absolute inset-0 z-10">
          <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-white/30">Loading experience...</div>}>
            <Car3D />
          </Suspense>
        </div>

        {/* Foreground content */}
        <div className="relative z-20 max-w-[1500px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20 min-h-screen flex flex-col justify-between pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="max-w-2xl pointer-events-auto"
          >
            <div className="overline flex items-center gap-3">
              <span className="w-8 h-[1px] bg-white/40" />
              {settings.city_region}
            </div>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-6 tracking-tighter leading-[0.9]">
              Quality Used Cars.
              <br />
              <span className="text-[var(--primary)]">Straight Talk.</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-md leading-relaxed">
              A used car dealer on Gallatin Pike serving the greater Nashville area.
              Come by, take a test drive, and buy at your own pace.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="flex flex-wrap items-center gap-4 pointer-events-auto mt-10"
          >
            <Link to="/inventory" data-testid="hero-cta-browse" className="btn-primary">
              Browse Inventory <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="hero-cta-visit" className="btn-outline">
              Book a Visit
            </Link>
            <div className="text-xs text-white/40 ml-2 hidden md:block">
              Drag the car → interact
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-white/10 py-8 overflow-hidden bg-[#0a0a0d]">
        <div className="marquee-track">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              {["Toyota", "Honda", "Ford", "Chevrolet", "Nissan", "Hyundai", "Jeep", "GMC", "Ram"].map((brand) => (
                <span key={brand + i} className="font-display text-4xl text-white/25 whitespace-nowrap tracking-tighter">{brand}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Featured Inventory */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="overline">Featured Machines</div>
            <h2 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">
              This week on
              <br />
              the showroom floor.
            </h2>
          </div>
          <Link to="/inventory" className="btn-outline self-start" data-testid="home-view-all">
            View all inventory <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.slice(0, 6).map((v, i) => (
            <VehicleCard key={v.id} vehicle={v} index={i} />
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/10 bg-[#0a0a0d]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 grid grid-cols-2 md:grid-cols-4 gap-y-10">
          {stats(settings).map((s) => (
            <div key={s.v} className="border-l border-white/10 pl-6">
              <div className="font-display text-5xl md:text-6xl text-[var(--primary)] tracking-tighter">{s.k}</div>
              <div className="overline mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-2xl">
          <div className="overline">Why Pick Auto</div>
          <h2 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">
            Honest. Local. Easy.
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed max-w-lg">
            We're a locally-owned used car dealership right here in Madison, TN.
            Come see the car, drive it, and take the time you need to make the call.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {[
            { icon: ShieldCheck, title: "See Before You Buy", body: "Every car is on the lot. Come inspect it in person, take a test drive, and check the history." },
            { icon: Sparkles, title: "No-Pressure Visits", body: "Sit in it. Drive it. Sleep on it. Walk away if it's not right. That's the deal." },
            { icon: Wrench, title: "Local & Reachable", body: `One phone call, one address, real people. Call ${settings.phone} or stop by Gallatin Pike.` },
          ].map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bento-cell"
              data-testid={`feature-${i}`}
            >
              <f.icon size={22} className="text-[var(--primary)]" />
              <h3 className="font-display text-3xl mt-6 tracking-tight">{f.title}</h3>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 pb-24">
        <div className="bento-cell md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-10 relative overflow-hidden">
          <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[var(--primary)]/20 blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-xl">
            <div className="overline">Ready to see it in person?</div>
            <h3 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">
              Stop by the lot
              <br />
              today.
            </h3>
            <p className="text-white/60 text-sm mt-4">
              {settings.address_line1}, {settings.address_line2} · Open {settings.hours_short}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 relative z-10">
            <Link to="/contact" className="btn-primary" data-testid="cta-book">Get Directions</Link>
            <Link to="/finance" className="btn-outline">Calculate Payment</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
